import { z } from 'zod';

import {
  patientPortalMfaEnrollConfirmResponseSchema,
  patientPortalMfaEnrollResponseSchema,
  patientPortalRegisterStartResponseSchema,
  patientPortalSessionResponseSchema,
  type PatientPortalIdentity,
  type PatientPortalLoginRequest,
  type PatientPortalMfaEnrollConfirmRequest,
  type PatientPortalMfaEnrollResponse,
  type PatientPortalRegisterStartRequest,
  type PatientPortalRegisterVerifyRequest,
} from '@emr/shared';

import { apiRequest } from '../client.ts';

/**
 * The patient portal's identity/session surface (`BACKLOG.md` W3, `server/src/patientAuth/
 * routes.ts`) — a separate module from `web/src/api/auth.ts`, the same "do not extend the staff
 * session model to patients" reasoning `docs/decisions/patient-portal-scope.md` states for the
 * server side, restated on the client: this surface has its own cookie
 * (`emr_patient_portal_session`), its own request/response shapes, and its own failure vocabulary
 * (`../patientPortal/errors.ts`'s `patientPortalErrorMessage`), none of which should be threaded
 * through the staff module's types.
 */

export type PatientSession = PatientPortalIdentity;

/** `POST /api/patient-portal/register/start` — always the same response shape regardless of what
 * matched server-side (`patientPortalRegisterStartResponseSchema`'s own header: no account-existence
 * oracle). */
export async function startPatientRegistration(
  input: PatientPortalRegisterStartRequest,
): Promise<{ readonly registrationId: string }> {
  return apiRequest(
    '/api/patient-portal/register/start',
    patientPortalRegisterStartResponseSchema,
    { method: 'POST', body: input },
  );
}

/** `POST /api/patient-portal/register/verify` — completes registration and starts a session. */
export async function verifyPatientRegistration(
  input: PatientPortalRegisterVerifyRequest,
): Promise<PatientSession> {
  const response = await apiRequest(
    '/api/patient-portal/register/verify',
    patientPortalSessionResponseSchema,
    { method: 'POST', body: input },
  );
  return response.patient;
}

/** `POST /api/patient-portal/auth/login`. */
export async function loginPatientPortal(
  input: PatientPortalLoginRequest,
): Promise<PatientSession> {
  const response = await apiRequest(
    '/api/patient-portal/auth/login',
    patientPortalSessionResponseSchema,
    { method: 'POST', body: input },
  );
  return response.patient;
}

/** `POST /api/patient-portal/auth/logout` — same "always succeeds from the client's point of view"
 * reasoning as `web/src/api/auth.ts`'s own `logout`. */
export function logoutPatientPortal(): Promise<unknown> {
  return apiRequest('/api/patient-portal/auth/logout', z.unknown(), { method: 'POST' });
}

/** `GET /api/patient-portal/auth/me` — the current session, or 401. */
export async function fetchPatientPortalSession(signal?: AbortSignal): Promise<PatientSession> {
  const response = await apiRequest(
    '/api/patient-portal/auth/me',
    patientPortalSessionResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.patient;
}

/** `POST /api/patient-portal/mfa/enroll` — begin TOTP enrollment (optional for v1, per
 * `docs/decisions/patient-portal-scope.md`'s "MFA" section). */
export function startPatientMfaEnrollment(): Promise<PatientPortalMfaEnrollResponse> {
  return apiRequest('/api/patient-portal/mfa/enroll', patientPortalMfaEnrollResponseSchema, {
    method: 'POST',
  });
}

/** `POST /api/patient-portal/mfa/enroll/confirm` — returns the one-time recovery codes. */
export async function confirmPatientMfaEnrollment(
  input: PatientPortalMfaEnrollConfirmRequest,
): Promise<readonly string[]> {
  const response = await apiRequest(
    '/api/patient-portal/mfa/enroll/confirm',
    patientPortalMfaEnrollConfirmResponseSchema,
    { method: 'POST', body: input },
  );
  return response.recoveryCodes;
}
